import { alpha } from "@mui/material/styles"
import { Box, Button, Card, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { capitalize, formatarBRL, formatarData } from "@/lib/formato"
import { useDispatch, useSelector } from "react-redux"
import { resetarView, trocarView } from "./navegacao-kiosk"
import type { ReactNode } from "react"
import type { RootState } from "@/store"
import type { PedidoEmAberto } from "@/store/self-service-kiosk-slice"
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople'
import ReceiptIcon from '@mui/icons-material/Receipt'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu'
import TableRestaurantOutlinedIcon from '@mui/icons-material/TableRestaurantOutlined'
import ScreenHeader from "./components/screen-header"
import { calcularConta } from "./items-screen/calculo-conta"
import AvisoInatividade from "@/components/totem/aviso-inatividade"
import { MENSAGEM_BLOQUEIO_TOTEM } from "@/api"

const INATIVIDADE_MS = 120_000

function InfoPill({ icon, children }: { icon: ReactNode; children: ReactNode }): JSX.Element {
    return (
        <Stack
            direction="row"
            spacing={0.75}
            sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                alignItems: 'center',
                backgroundColor: 'action.hover',
            }}
        >
            {icon}

            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {children}
            </Typography>
        </Stack>
    )
}

interface OrderCardProps {
    pedido: PedidoEmAberto
    rotuloOposto: string
    onSelect: (pedido: PedidoEmAberto) => void
}

function OrderCard({ pedido, rotuloOposto, onSelect }: OrderCardProps): JSX.Element {
    const itens = pedido.itens ?? []
    const abertoAs = pedido.dataPedido ? formatarData(pedido.dataPedido, 'hora') : null
    const bloqueado = pedido.bloqueadoNoTotem === true
    const { total, valorRecebido, pendente } = calcularConta(pedido)
    const temPagamentoParcial = valorRecebido > 0 && pendente > 0

    return (
        <Card
            elevation={0}
            sx={{
                gap: 2,
                p: 2.5,
                display: 'flex',
                cursor: 'default',
                borderRadius: 3,
                border: '1.5px solid',
                borderColor: 'divider',
                flexDirection: 'column',
                backgroundColor: 'background.paper',
                height: { xs: 'max-content', md: '100%' },
            }}
        >
            <Stack direction="row" spacing={2} sx={{ alignItems: 'start' }}>
                <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Stack spacing={0.3}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }} noWrap>
                            {pedido.nomeCliente ? capitalize(pedido.nomeCliente) : 'Cliente não informado'}
                        </Typography>

                        {pedido.sequencial != null && (
                            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                                <ReceiptIcon sx={{ fontSize: 16 }} />

                                <Typography variant="h6" noWrap>
                                    Pedido #{pedido.sequencial}
                                </Typography>
                            </Stack>
                        )}

                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                            <TableRestaurantOutlinedIcon sx={{ fontSize: 16 }} />

                            <Typography variant="h6">
                                {pedido.identificadorSecundario
                                    ? `${rotuloOposto} ${pedido.identificadorSecundario}`
                                    : `Sem ${rotuloOposto.toLowerCase()}`}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>

                {abertoAs && (
                    <InfoPill icon={<AccessTimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}>
                        Aberto às {abertoAs}
                    </InfoPill>
                )}

                <InfoPill icon={<RestaurantMenuIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}>
                    {itens.length} {itens.length === 1 ? 'item' : 'itens'}
                </InfoPill>
            </Stack>

            {itens.length > 0 && (
                <>
                    <Divider sx={{ borderStyle: 'dashed' }}>
                        <Typography variant="h6">
                            {itens.length > 1 ? 'Itens do pedido' : 'Item do pedido'}
                        </Typography>
                    </Divider>

                    <Stack spacing={0.5}>
                        {itens.map((item, indice) => (
                            <Stack
                                key={`${item.codigoExterno ?? 'item'}-${indice}`}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: 'center' }}
                            >
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ fontWeight: 'bold', flexShrink: 0 }}
                                >
                                    {item.quantidade ?? 1}×
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ flexGrow: 1, minWidth: 0 }}
                                >
                                    {capitalize(item.nome ?? '')}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 600, flexShrink: 0, color: 'success.main' }}
                                >
                                    {formatarBRL(item.valorTotal)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </>
            )}

            <Divider sx={{ borderStyle: 'dashed', mt: 'auto' }} />

            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack spacing={0.5}>
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {temPagamentoParcial ? 'Falta pagar' : 'Total do pedido'}
                    </Typography>

                    {temPagamentoParcial && (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                            <Typography variant="body2" color="text.secondary">
                                Total {formatarBRL(total)}
                            </Typography>

                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                − {formatarBRL(valorRecebido)} já pago
                            </Typography>
                        </Stack>
                    )}

                    <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                        {formatarBRL(temPagamentoParcial ? pendente : total)}
                    </Typography>
                </Stack>

                {bloqueado ? (
                    <Stack spacing={0} sx={{ textAlign: 'right', flexShrink: 0, maxWidth: '55%' }}>
                        <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold' }}>
                            Indisponível no totem
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                            {pedido.motivoBloqueio ? MENSAGEM_BLOQUEIO_TOTEM[pedido.motivoBloqueio] : 'Fale com o atendente.'}
                        </Typography>
                    </Stack>
                ) : (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onSelect(pedido)}
                        startIcon={<EmojiPeopleIcon />}
                        sx={{ py: 1.5, px: 3, borderRadius: 3, textTransform: 'none', flexShrink: 0 }}
                    >
                        Este é o meu
                    </Button>
                )}
            </Stack>
        </Card>
    )
}

export default function MultipleOrdersScreen(): JSX.Element {
    const dispatch = useDispatch()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const pedidos = useSelector((state: RootState) => state.selfServiceKiosk.multipleOrders)
    const modeloIdentificador = useSelector((state: RootState) => state.selfServiceKiosk.modeloIdentificador)
    const identificadorBuscado = useSelector((state: RootState) => state.selfServiceKiosk.identificadorBuscado)
    const rotuloPrincipal = capitalize(modeloIdentificador)
    const rotuloOposto = modeloIdentificador === 'COMANDA' ? 'Mesa' : 'Comanda'

    const onClickBack = (): void => {
        trocarView(dispatch, { currentView: 'VIEW_IDENTIFIER', order: null, multipleOrders: [] })
    }

    const onSelectOrder = (pedido: PedidoEmAberto): void => {
        trocarView(dispatch, { currentView: 'VIEW_ITEMS', order: pedido, hasMultipleOrders: true })
    }

    const nenhumSelecionavel = pedidos.length > 0 && pedidos.every((p) => p.bloqueadoNoTotem === true)

    return (
        <Stack sx={{ width: '100%', height: '100vh', overflow: 'hidden', boxSizing: 'border-box' }}>
            <ScreenHeader
                title="Selecione seu pedido"
                subTitle={
                    `${rotuloPrincipal} ${identificadorBuscado} · ${pedidos.length}${' '}
                    ${pedidos.length === 1 ? 'pedido encontrado' : 'pedidos encontrados'}`
                }
                onClickBack={onClickBack}
            />

            {pedidos.length > 0 ? (
                <Stack sx={{ flexGrow: 1, overflow: 'hidden', p: 2, pt: 0, gap: 2, minHeight: 0 }}>
                    <Box
                        sx={{
                            p: 2,
                            gap: 2,
                            display: 'flex',
                            borderRadius: 3,
                            border: '1px solid',
                            alignItems: 'center',
                            borderColor: (t) => alpha(t.palette.primary.main, 0.4),
                            backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
                        }}
                    >
                        <Box
                            sx={{
                                width: 52,
                                height: 52,
                                flexShrink: 0,
                                display: 'flex',
                                borderRadius: '50%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'primary.contrastText',
                                backgroundColor: 'primary.main',
                            }}
                        >
                            <ReceiptLongOutlinedIcon sx={{ fontSize: 30 }} />
                        </Box>

                        <Stack spacing={0.25}>
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                {nenhumSelecionavel
                                    ? `Nenhum pedido desta ${rotuloPrincipal.toLowerCase()} pode ser atendido no totem`
                                    : `Encontramos mais de um pedido para esta ${rotuloPrincipal.toLowerCase()}`}
                            </Typography>

                            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {nenhumSelecionavel
                                    ? 'Procure o atendente para fechar a conta.'
                                    : 'Toque em "Este é o meu" no pedido correto para continuar.'}
                            </Typography>
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            gap: 2,
                            flexGrow: 1,
                            display: 'grid',
                            overflowY: 'auto',
                            alignContent: 'start',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                            minHeight: 0,
                            pr: 1
                        }}
                    >
                        {pedidos.map((pedido, indice) => (
                            <OrderCard
                                key={pedido.codigoIntegracao ?? indice}
                                pedido={pedido}
                                rotuloOposto={rotuloOposto}
                                onSelect={onSelectOrder}
                            />
                        ))}
                    </Box>

                    <AvisoInatividade ativo msInatividade={INATIVIDADE_MS} aoExpirar={() => resetarView(dispatch)} />
                </Stack>
            ) : (
                <Stack sx={{ flexGrow: 1, alignItems: 'center', justifyContent: 'center', gap: 2, p: 2 }}>
                    <ReceiptLongOutlinedIcon sx={{ fontSize: 72, color: 'text.secondary' }} />

                    <Typography variant="h5" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Nenhum pedido para exibir.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onClickBack}
                        sx={{ py: 1.5, px: 4, borderRadius: 3, textTransform: 'none' }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Voltar
                        </Typography>
                    </Button>
                </Stack>
            )}
        </Stack>
    )
}